import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { Box, Button, Popover, Typography, useTheme } from '@mui/material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ILocality } from 'src/generated-types'
import { IMainComponent } from 'src/generated-types/main-component'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor } from 'src/utils'
import { EquipmentMetadataSection } from './EquipmentMetadataSection'

interface Props {
  equipment: IMainComponent
  locality?: ILocality
  customerId: EntityId
}

type SpecValue = number | string | null | undefined

const isPopulated = (value: SpecValue): boolean =>
  value != null && !(typeof value === 'string' && value.trim() === '')

const MAX_STRIP_SPECS = 4

export const EquipmentDetailsPopover = ({ equipment, locality, customerId }: Props) => {
  const { t } = useTranslation(['main-component', 'common'])
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const keySpecs = useMemo(() => {
    const depth = equipment.position?.depth
    const candidates: Array<{ label: string; value: SpecValue }> = [
      { label: t('supplier', { ns: 'common' }), value: equipment.supplier?.name },
      { label: t('productionYear', { ns: 'main-component' }), value: equipment.productionYear },
      { label: t('solidity', { ns: 'main-component' }), value: equipment.solidity },
      { label: t('circumference', { ns: 'main-component' }), value: equipment.circumference },
      { label: t('depth', { ns: 'common' }), value: depth != null ? `${depth} m` : undefined },
    ]
    return candidates.filter(spec => isPopulated(spec.value)).slice(0, MAX_STRIP_SPECS)
  }, [
    equipment.supplier?.name,
    equipment.productionYear,
    equipment.solidity,
    equipment.circumference,
    equipment.position?.depth,
    t,
  ])

  const open = Boolean(anchorEl)

  return (
    <Flex.Row alignItems="center" gap={1} sx={{ flexWrap: 'wrap', minWidth: 0 }}>
      {keySpecs.length > 0 && (
        <Typography
          component="span"
          sx={{
            fontSize: '12px',
            lineHeight: 1.4,
            color: getThemeColor(theme, MUIThemeColors.secondaryDark),
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {keySpecs.map((spec, index) => (
            <Box component="span" key={spec.label}>
              {index > 0 && <Box component="span" sx={{ mx: '6px' }}>·</Box>}
              <Box component="span">{`${spec.label}: `}</Box>
              <Box
                component="span"
                sx={{
                  fontWeight: 600,
                  color: getThemeColor(theme, MUIThemeColors.primaryDark),
                }}
              >
                {spec.value}
              </Box>
            </Box>
          ))}
        </Typography>
      )}
      <Button
        size="small"
        variant="outlined"
        onClick={event => setAnchorEl(event.currentTarget)}
        startIcon={<InfoOutlined sx={{ fontSize: '16px' }} />}
        sx={{
          fontSize: '12px',
          lineHeight: 1.2,
          py: '2px',
          px: '8px',
          minWidth: 0,
          textTransform: 'none',
          flexShrink: 0,
        }}
      >
        {t('details', { ns: 'common' })}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 520,
            maxWidth: '90vw',
            maxHeight: '70vh',
            overflowY: 'auto',
            p: '10px',
            borderRadius: '8px',
          },
        }}
      >
        <EquipmentMetadataSection equipment={equipment} locality={locality} customerId={customerId} />
      </Popover>
    </Flex.Row>
  )
}

export default EquipmentDetailsPopover
